import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Check,
  X,
  Loader2,
  Calendar,
  Bell,
  StickyNote,
  Clock,
  CheckSquare,
  Megaphone,
  CalendarDays,
  TrendingUp,
  Umbrella,
  Users,
  ClipboardCheck,
  BarChart3,
  FileText,
  AlertTriangle,
  Shield,
  Package,
  Truck,
  FileCheck,
  Activity,
  Building2,
  Lock,
  Maximize2,
  Trophy,
  Award,
  MessageSquare,
  Zap,
  Sparkles,
  Share2,
  DollarSign,
  Store,
  Send,
  ListTodo
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const ICON_MAP = {
  Calendar,
  Bell,
  StickyNote,
  Clock,
  CheckSquare,
  Megaphone,
  CalendarDays,
  TrendingUp,
  Umbrella,
  Users,
  ClipboardCheck,
  BarChart3,
  FileText,
  AlertTriangle,
  Shield,
  Package,
  Truck,
  FileCheck,
  Activity,
  Building2,
  Trophy,
  Award,
  MessageSquare,
  Zap,
  Sparkles,
  Share2,
  DollarSign,
  Store,
  Send,
  ListTodo,
};

export default function WidgetLibrary({ open, onClose, onWidgetsChange, currentWidgets }) {
  const [widgets, setWidgets] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [enabledWidgets, setEnabledWidgets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchWidgets();
      setEnabledWidgets(currentWidgets || []);
    }
  }, [open, currentWidgets]);

  const fetchWidgets = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/widgets/library');
      setWidgets(data.widgets || []);
      setCategories(data.categories || {});
    } catch (error) {
      toast.error('Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget?.compulsory) return; // Can't toggle compulsory widgets
    
    setEnabledWidgets(prev => {
      if (prev.includes(widgetId)) {
        return prev.filter(id => id !== widgetId);
      } else {
        return [...prev, widgetId];
      }
    });
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await apiRequest('/widgets/my-preferences', {
        method: 'PUT',
        body: JSON.stringify({
          enabled_widgets: enabledWidgets,
          widget_order: enabledWidgets
        })
      });
      toast.success('Dashboard updated');
      onWidgetsChange?.(enabledWidgets);
      onClose();
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const filteredWidgets = widgets.filter(widget => {
    const matchesSearch = !search || 
      widget.name.toLowerCase().includes(search.toLowerCase()) ||
      widget.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <CheckSquare className="h-5 w-5" />;
  };

  const categoryList = Object.entries(categories)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([id, cat]) => ({ id, ...cat }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Widget Library</DialogTitle>
          <DialogDescription>
            Choose widgets to display on your dashboard. Essential widgets cannot be removed.
          </DialogDescription>
        </DialogHeader>

        {/* Search & Filter */}
        <div className="flex gap-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search widgets..."
              className="pl-10"
              data-testid="widget-search"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">All Categories</option>
            {categoryList.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Widget Grid */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredWidgets.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Search className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No widgets found</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {filteredWidgets.map(widget => {
                const isEnabled = enabledWidgets.includes(widget.id);
                const isCompulsory = widget.compulsory;
                
                return (
                  <div
                    key={widget.id}
                    onClick={() => toggleWidget(widget.id)}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      isCompulsory
                        ? 'border-blue-300 bg-blue-50 cursor-default'
                        : isEnabled
                          ? 'border-green-500 bg-green-50 hover:border-green-600'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`widget-${widget.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        isCompulsory ? 'bg-blue-100 text-blue-600' :
                        isEnabled ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getIcon(widget.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{widget.name}</p>
                          {isCompulsory && (
                            <Lock className="h-3 w-3 text-blue-500" />
                          )}
                          {widget.expandable && (
                            <Maximize2 className="h-3 w-3 text-slate-400" />
                          )}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">{widget.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {categories[widget.category]?.name || widget.category}
                          </Badge>
                          {isCompulsory && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Essential</Badge>
                          )}
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isCompulsory
                          ? 'border-blue-500 bg-blue-500'
                          : isEnabled
                            ? 'border-green-500 bg-green-500'
                            : 'border-slate-300'
                      }`}>
                        {(isEnabled || isCompulsory) && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-slate-500">
            {enabledWidgets.length} widgets selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={savePreferences} 
              disabled={saving}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="save-widgets-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
